import React from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, formatNumber, tooltipStyle } from '../types';

interface GeographyTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = [
  'overview', 'platforms', 'countries', 'reg-types', 'reg-types-daily', 'cumulative-growth',
];

const GeographyTab: React.FC<GeographyTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  if (loading) return <LoadingSpinner />;

  const overview = data['overview'];
  const countries = data['countries'] || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <StatCard title="Unique Countries" value={overview?.uniqueCountries || 0} subtitle="Active user locations" />
        <StatCard title="Top Country" value={countries[0]?.country || '-'} subtitle={countries[0] ? formatNumber(countries[0].count) + ' users' : ''} />
      </div>

      <ChartCard title="Top 20 Countries by Active Users">
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={countries.slice(0, 20)} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(v) => formatNumber(v)} />
            <YAxis type="category" dataKey="country" stroke="#64748b" fontSize={11} tickLine={false} width={100} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#FF9C15" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="All Countries">
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {countries.map((c: any, i: number) => {
            const maxCount = countries[0]?.count || 1;
            const percentage = (c.count / maxCount) * 100;
            return (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-6 text-right">{i + 1}</span>
                <span className="text-sm text-white w-32 truncate">{c.country}</span>
                <div className="flex-1 bg-white/5 rounded-full h-6 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#FF9C15] to-[#F42026] rounded-full flex items-center justify-end pr-2"
                    style={{ width: `${Math.max(percentage, 5)}%` }}
                  >
                    <span className="text-xs text-white font-medium">{formatNumber(c.count)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ChartCard>
    </div>
  );
};

export default GeographyTab;
